import { useMemo } from "react";
import DoughnutChart from "../../../charts/DoughnutChart";
import { tailwindConfig } from "../../../utils/Utils";
import { TestQuestion, TestSection } from "../types/test-detail.types";

import Button, { ButtonVariant } from "@shared/components/ui/Button";
import { BookIcon, RefreshIcon } from "@shared/components/ui/Icon";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import RestartTestModal from "./RestartTestModal";
import TextRenderer from "./TextRenderer";

interface TestResultsProps {
  sections: TestSection[];
  onRestart: () => Promise<void>;
  isRestarting: boolean;
  answers: Array<{
    questionId: string;
    selectedOption: string;
    isCorrect: boolean;
    correctAnswer?: string;
  }>;
  score: number;
  correctAnswers: number;
  incorrectAnswers: number;
  correctOption?: string;
  testProgressId?: string;
}

export default function TestResults({
  sections,
  answers,
  score,
  correctAnswers,
  incorrectAnswers,
  onRestart,
  isRestarting,
  testProgressId,
}: TestResultsProps) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isGeneratingStudyPlan, setIsGeneratingStudyPlan] = useState(false);
  const navigate = useNavigate();

  const handleRestart = () => {
    setShowConfirmModal(true);
  };

  const confirmRestart = async () => {
    setShowConfirmModal(false);
    await onRestart();
  };

  const handleGenerateStudyPlan = async () => {
    if (!testProgressId) return;

    setIsGeneratingStudyPlan(true);
    try {
      // Navigate to study plan page with the test progress ID as a path parameter
      navigate(`/study-plan/${testProgressId}`);
    } catch (error) {
      console.error("Error navigating to study plan:", error);
      setIsGeneratingStudyPlan(false);
    }
  };

  const questionsWithResults = useMemo(() => {
    const results: Array<{
      section: TestSection;
      question: TestQuestion;
      answer: (typeof answers)[0];
    }> = [];

    sections.forEach((section) => {
      section.questions.forEach((question) => {
        const answer = answers.find((a) => a.questionId === question.id);
        if (answer) {
          results.push({
            section,
            question,
            answer,
          });
        }
      });
    });

    return results;
  }, [sections, answers]);

  return (
    <div className="y-8">
      <RestartTestModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmRestart}
        isRestarting={isRestarting}
      />
      {/* Summary Card */}
      <div className={`${theme.bg.primary} rounded-lg shadow p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Test Results</h2>
          <div className="flex space-x-3">
            {testProgressId && (
              <Button
                onClick={handleGenerateStudyPlan}
                variant={ButtonVariant.SECONDARY}
                disabled={isGeneratingStudyPlan}
                leftIcon={<BookIcon />}
              >
                {isGeneratingStudyPlan
                  ? "Generating..."
                  : "Generate Study Plan"}
              </Button>
            )}
            <Button
              onClick={handleRestart}
              disabled={isRestarting}
              leftIcon={<RefreshIcon />}
            >
              {isRestarting ? "Restarting..." : "Restart Test"}
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Stats */}
          <div className="space-y-4">
            <div>
              <div className="text-4xl font-bold text-violet-600">{score}%</div>
              <div className={theme.text.muted}>Final Score</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-semibold text-emerald-500">
                  {correctAnswers}
                </div>
                <div className={`text-sm ${theme.text.muted}`}>
                  Correct Answers
                </div>
              </div>
              <div>
                <div className={`text-2xl font-semibold ${theme.text.error}`}>
                  {incorrectAnswers}
                </div>
                <div className={`text-sm ${theme.text.muted}`}>
                  Incorrect Answers
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="w-48 mx-auto">
            <DoughnutChart
              data={{
                labels: ["Correct", "Incorrect"],
                datasets: [
                  {
                    data: [correctAnswers, incorrectAnswers],
                    backgroundColor: [
                      tailwindConfig().theme.colors.green[500],
                      tailwindConfig().theme.colors.red[500],
                    ],
                    hoverBackgroundColor: [
                      tailwindConfig().theme.colors.green[600],
                      tailwindConfig().theme.colors.red[600],
                    ],
                    borderWidth: 0,
                  },
                ],
              }}
              width={389}
              height={160}
            />
          </div>
        </div>
      </div>

      {/* Questions Review */}
      <div className="space-y-6 mt-6">
        <h3 className="text-xl font-semibold">Questions Review</h3>

        <div className="grid grid-cols-12 gap-6">
          {questionsWithResults.map(({ section, question, answer }) => (
            <div
              key={question.id}
              className={`${theme.bg.primary} rounded-lg shadow p-6 col-span-12 md:col-span-6`}
            >
              {/* Section info if available */}
              {section.header?.title && (
                <div className={`text-sm ${theme.text.muted}`}>
                  {section.header.title}
                </div>
              )}

              {/* Question */}
              <div className="mb-4">
                <div className="flex items-start">
                  <span className="font-medium mr-2">{question.number}.</span>
                  <TextRenderer
                    text={question.text}
                    allowHtml={false}
                    allowedTags={[
                      "sup",
                      "sub",
                      "b",
                      "i",
                      "em",
                      "strong",
                      "span",
                    ]}
                    allowedAttr={["class"]}
                  />
                </div>
              </div>

              {/* Answer Section */}
              <div className="space-y-3">
                {question.options?.length > 0 ? (
                  // Multiple Choice Questions
                  question.options.map((option) => {
                    const isSelected = option.id === answer.selectedOption;
                    const isCorrect = isSelected && answer.isCorrect;
                    const isCorrectOption = option.id === answer.correctAnswer;

                    let optionClass = "border p-3 rounded-lg flex items-center";

                    if (isCorrectOption) {
                      optionClass +=
                        " bg-emerald-50 border-emerald-500 text-emerald-700";
                    } else if (isSelected && isCorrect) {
                      optionClass +=
                        " bg-emerald-50 border-emerald-500 text-emerald-700";
                    } else if (isSelected && !isCorrect) {
                      optionClass += " bg-red-50 border-red-500 text-red-700";
                    } else {
                      optionClass += " border-gray-200 text-gray-700";
                    }

                    return (
                      <div key={option.id} className={optionClass}>
                        <span className="font-medium mr-2">
                          {option.option}.
                        </span>
                        <TextRenderer
                          text={option.text}
                          allowHtml={true}
                          allowedTags={[
                            "sup",
                            "sub",
                            "b",
                            "i",
                            "em",
                            "strong",
                            "span",
                          ]}
                          allowedAttr={["class"]}
                        />
                        {isSelected && isCorrect && (
                          <span className="ml-auto">✓</span>
                        )}
                        {isSelected && !isCorrect && (
                          <span className="ml-auto">✗</span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  // Free Text Questions
                  <div className="space-y-4">
                    {/* User's Answer */}
                    <div className="space-y-2">
                      <div
                        className={`text-sm font-medium ${theme.text.muted}`}
                      >
                        Your answer:
                      </div>
                      <div
                        className={`border p-3 rounded-lg ${
                          answer.isCorrect
                            ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                            : "bg-red-50 ${theme.border.error} text-red-700"
                        }`}
                      >
                        {answer.selectedOption || "No answer provided"}
                        <span className="ml-2">
                          {answer.isCorrect ? "✓" : "✗"}
                        </span>
                      </div>
                    </div>

                    {/* Correct Answer */}
                    {!answer.isCorrect && answer.correctAnswer && (
                      <div className="space-y-2">
                        <div
                          className={`text-sm font-medium ${theme.text.muted}`}
                        >
                          Correct answer:
                        </div>
                        <div className="border p-3 rounded-lg bg-emerald-50 border-emerald-500 text-emerald-700">
                          {answer.correctAnswer}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Explanation Section - Show if available */}
              {question.explanation && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div
                    className={`text-sm font-medium ${theme.text.muted} mb-2`}
                  >
                    Explanation:
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300">
                    <TextRenderer
                      text={question.explanation}
                      allowedAttr={["class"]}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
